import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, Building2, HeartHandshake, Tags, Plus, Trash2, Search, ShieldCheck, KeyRound, CalendarClock, Globe, Bell, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { InitiativeCard } from "./Initiatives";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { fileUrl } from "../lib/api";
import { PROFESSIONS, PROFESSION_CATEGORIES, professionCategoryLabel, specialtyLabel, localize, countryLabel } from "../lib/meta";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

export default function AdminArea() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("psychologists");
  const [psychStatus, setPsychStatus] = useState("pending");

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const openPendingQueue = () => { setPsychStatus("pending"); setTab("psychologists"); };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("admin.title")}</h1>

      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <button onClick={openPendingQueue} data-testid="admin-stat-pending"
            className="rounded-2xl border-2 border-terracotta/30 bg-terracotta-light/40 p-5 text-left transition-colors hover:border-terracotta">
            <p className="font-display text-3xl font-bold text-terracotta">{stats.psychologists_pending}</p>
            <p className="mt-1 text-xs font-medium text-slateink">{t("admin.stat_awaiting_review")}</p>
            <p className="mt-1 text-[11px] text-terracotta underline">{t("admin.open_review_queue")}</p>
          </button>
          {[[t("status.changes"), stats.psychologists_changes], [t("admin.stats_verified"), stats.psychologists_verified], [t("admin.stats_org_pending"), stats.organizers_pending], [t("admin.stats_initiatives"), stats.initiatives]].map(([l, v], idx) => (
            <div key={idx} className="rounded-2xl border border-border bg-white p-5" data-testid={`admin-stat-${idx}`}>
              <p className="font-display text-3xl font-bold text-slateink">{v}</p>
              <p className="mt-1 text-xs text-muted-foreground">{l}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="psychologists" data-testid="admin-tab-psychologists"><Users className="mr-1.5 h-4 w-4" />{t("admin.professionals")}</TabsTrigger>
          <TabsTrigger value="organizers" data-testid="admin-tab-organizers"><Building2 className="mr-1.5 h-4 w-4" />{t("admin.organizers")}</TabsTrigger>
          <TabsTrigger value="initiatives" data-testid="admin-tab-initiatives"><HeartHandshake className="mr-1.5 h-4 w-4" />{t("admin.initiatives")}</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings"><CalendarClock className="mr-1.5 h-4 w-4" />{t("admin.bookings")}</TabsTrigger>
          <TabsTrigger value="areas" data-testid="admin-tab-areas"><Tags className="mr-1.5 h-4 w-4" />{t("admin.areas")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security"><ShieldCheck className="mr-1.5 h-4 w-4" />{t("admin.security")}</TabsTrigger>
        </TabsList>
        <TabsContent value="psychologists" className="mt-6"><AdminPsychologists status={psychStatus} setStatus={setPsychStatus} onChanged={loadStats} /></TabsContent>
        <TabsContent value="organizers" className="mt-6"><AdminOrganizers /></TabsContent>
        <TabsContent value="initiatives" className="mt-6"><AdminInitiatives onChanged={loadStats} /></TabsContent>
        <TabsContent value="bookings" className="mt-6"><AdminBookings /></TabsContent>
        <TabsContent value="areas" className="mt-6"><AdminAreas lang={lang} /></TabsContent>
        <TabsContent value="security" className="mt-6"><AdminSecurity /></TabsContent>
      </Tabs>
    </div>
  );
}

function AdminPsychologists({ status, setStatus, onChanged }) {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [profession, setProfession] = useState("all");
  const [group, setGroup] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (profession !== "all") params.profession = profession;
    if (group !== "all") params.profession_group = group;
    if (q) params.q = q;
    api.get("/admin/psychologists", { params }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [status, q, profession, group]);
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [load]);

  const act = async (id, s) => { await api.patch(`/admin/psychologists/${id}/status`, { status: s }); load(); onChanged?.(); toast.success(t(`status.${s}`)); };
  const requestChanges = async (id) => { const note = window.prompt(t("admin.note_prompt")); if (!note || !note.trim()) return; await api.patch(`/admin/psychologists/${id}/status`, { status: "changes", note: note.trim() }); load(); onChanged?.(); toast.success(t("status.changes")); };
  const del = async (id) => {
    try {
      await api.delete(`/admin/psychologists/${id}`);
      load(); onChanged?.(); toast.success(t("del.admin_success"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.error === "has_linked_activity" ? t("del.blocked_body") : t("del.error"));
    }
  };

  const filters = [["pending", t("admin.pending")], ["changes", t("status.changes")], ["verified", t("admin.verified")], ["all", t("admin.all")]];
  const categories = [["all", t("admin.all_categories")], ...PROFESSION_CATEGORIES.map((c) => [c.value, professionCategoryLabel(c.value, lang)])];

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-prof-categories">
        {categories.map(([v, l]) => (
          <button key={v} onClick={() => setGroup(v)} data-testid={`admin-cat-${v}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${group === v ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-slateink/70 hover:border-terracotta/40"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full border border-border bg-white p-1">
          {filters.map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} data-testid={`admin-psych-filter-${v}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium ${status === v ? "bg-slateink text-white" : "text-slateink/70"}`}>{l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-psych-search" />
        </div>
        <Select value={profession} onValueChange={setProfession}>
          <SelectTrigger className="w-full sm:w-[210px]" data-testid="admin-psych-profession-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.all_professions")}</SelectItem>
            {PROFESSIONS.map((p) => <SelectItem key={p.value} value={p.value}>{localize(PROFESSIONS, p.value, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5">
        {list.length === 0 ? <EmptyState icon={Users} title={t("admin.no_psychologists")} testId="admin-psych-empty" /> : (
          <div className="space-y-3">
            {list.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5" data-testid={`admin-psych-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 overflow-hidden rounded-xl bg-secondary">
                    {p.photo_path ? <img src={fileUrl(p.photo_path)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-bold text-slateink/30">{(p.full_name||"?").charAt(0)}</div>}
                  </div>
                  <div>
                    <p className="font-medium text-slateink">{p.full_name}</p>
                    <p className="text-xs text-muted-foreground">{p.title} · {p.email}</p>
                  </div>
                </div>
                {(p.license_number || p.license_org || p.license_country || p.credential_type) && (
                  <div className="w-full rounded-lg border border-terracotta/30 bg-cream px-3 py-2 text-xs text-slateink" data-testid={`admin-psych-credentials-${p.id}`}>
                    <span className="font-semibold text-terracotta">{t("admin.credentials")}:</span>{" "}
                    {[p.credential_type && `${t("admin.cred_type")}: ${p.credential_type}`,
                      p.license_number && `${t("admin.cred_license")} ${p.license_number}`,
                      p.license_org, countryLabel(p.license_country, lang)].filter(Boolean).join(" · ")}
                  </div>
                )}
                {p.profession_category === "non_clinical" && (
                  <div className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-slateink" data-testid={`admin-psych-quals-${p.id}`}>
                    <span className="font-semibold">{t("admin.qualifications")}:</span>{" "}
                    {p.qualifications || "—"}{p.years_experience != null ? ` · ${p.years_experience} ${t("admin.years_short")}` : ""}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {p.profession_group && <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground" data-testid={`admin-psych-group-${p.id}`}>{professionCategoryLabel(p.profession_group, lang)}</span>}
                  {p.profession && <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground" data-testid={`admin-psych-profession-${p.id}`}>{(p.profession === "other_clinical" || p.profession === "other_non_clinical") && p.profession_other ? p.profession_other : localize(PROFESSIONS, p.profession, lang)}</span>}
                  {(p.specialties || []).length > 0 && <span className="text-xs text-muted-foreground" data-testid={`admin-psych-specialties-${p.id}`}>{p.specialties.map((s) => specialtyLabel(s, lang)).join(" · ")}</span>}
                  <StatusBadge status={p.status} label={t(`status.${p.status}`)} />
                  {p.status === "changes" && p.changes_requested_note && <p className="w-full rounded-lg bg-terracotta-light px-3 py-2 text-xs text-slateink/80" data-testid={`changes-note-${p.id}`}><b>{t("status.changes")}:</b> {p.changes_requested_note}</p>}
                  {p.status !== "verified" && <Button size="sm" onClick={() => act(p.id, "verified")} className="bg-[#1E6B47] hover:bg-[#155235]" data-testid={`approve-${p.id}`}>{t("common.approve")}</Button>}
                  <Button size="sm" variant="outline" onClick={() => requestChanges(p.id)} data-testid={`request-changes-${p.id}`}>{t("admin.request_changes")}</Button>
                  {p.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => act(p.id, "rejected")} data-testid={`reject-${p.id}`}>{t("common.reject")}</Button>}
                  {p.status === "verified" && <Button size="sm" variant="ghost" onClick={() => act(p.id, "suspended")} data-testid={`suspend-${p.id}`}>{t("admin.suspend")}</Button>}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-white" data-testid={`delete-${p.id}`}>
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />{t("del.admin_button")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`delete-dialog-${p.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("del.admin_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("del.admin_confirm_body")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`delete-cancel-${p.id}`}>{t("del.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(p.id)} className="bg-destructive text-white hover:bg-destructive/90" data-testid={`delete-confirm-${p.id}`}>{t("del.confirm_action")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminOrganizers() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/organizers").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const act = async (id, s) => { await api.patch(`/admin/organizers/${id}/status`, { status: s }); load(); toast.success(t(`status.${s}`)); };

  if (list.length === 0) return <EmptyState icon={Building2} title={t("admin.no_organizers")} testId="admin-org-empty" />;
  return (
    <div className="space-y-3">
      {list.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5" data-testid={`admin-org-${o.id}`}>
          <div>
            <p className="font-medium text-slateink">{o.name}</p>
            <p className="text-xs text-muted-foreground">{o.email} · {o.initiative_count} {t("admin.initiatives").toLowerCase()}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={o.organizer_status || "pending"} label={t(`status.${o.organizer_status || "pending"}`)} />
            {o.organizer_status !== "approved" && <Button size="sm" onClick={() => act(o.id, "approved")} className="bg-[#1E6B47] hover:bg-[#155235]" data-testid={`approve-org-${o.id}`}>{t("common.approve")}</Button>}
            {o.organizer_status !== "restricted" && <Button size="sm" variant="outline" onClick={() => act(o.id, "restricted")} data-testid={`restrict-org-${o.id}`}>{t("admin.restrict")}</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminInitiatives({ onChanged }) {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const load = useCallback(() => { api.get("/admin/initiatives").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    try {
      await api.delete(`/initiatives/${id}`);
      toast.success(t("admin.initiative_deleted"));
      load(); onChanged?.();
    } catch {
      toast.error(t("del.error"));
    }
  };
  if (list.length === 0) return <EmptyState icon={HeartHandshake} title={t("initiatives.empty_title")} testId="admin-init-empty" />;
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {list.map((i) => (
        <div key={i.id} className="relative" data-testid={`admin-init-${i.id}`}>
          <InitiativeCard i={i} />
          <div className="mt-2 flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-white" data-testid={`admin-init-delete-${i.id}`}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />{t("admin.delete_initiative")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid={`admin-init-delete-dialog-${i.id}`}>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("admin.delete_init_title")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("admin.delete_init_body")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`admin-init-delete-cancel-${i.id}`}>{t("del.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => del(i.id)} className="bg-destructive text-white hover:bg-destructive/90" data-testid={`admin-init-delete-confirm-${i.id}`}>{t("del.confirm_action")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminAreas({ lang }) {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [es, setEs] = useState(""); const [en, setEn] = useState("");
  const load = () => api.get("/areas").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); if (!es || !en) return; await api.post("/areas", { name_es: es, name_en: en }); setEs(""); setEn(""); load(); };
  const del = async (id) => { await api.delete(`/areas/${id}`); load(); };
  return (
    <div>
      <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-white p-5">
        <div className="flex-1 min-w-[140px]"><label className="text-xs text-muted-foreground">{t("admin.area_es")}</label><Input value={es} onChange={(e) => setEs(e.target.value)} data-testid="area-es-input" /></div>
        <div className="flex-1 min-w-[140px]"><label className="text-xs text-muted-foreground">{t("admin.area_en")}</label><Input value={en} onChange={(e) => setEn(e.target.value)} data-testid="area-en-input" /></div>
        <Button type="submit" className="bg-terracotta hover:bg-terracotta-hover" data-testid="add-area-btn"><Plus className="mr-1 h-4 w-4" />{t("admin.add_area")}</Button>
      </form>
      <div className="mt-4 flex flex-wrap gap-2">
        {list.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-sm" data-testid={`area-item-${a.id}`}>
            {lang === "es" ? a.name_es : (a.name_en || a.name_es)}
            <button onClick={() => del(a.id)} data-testid={`delete-area-${a.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}


function AdminSecurity() {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== cf) { toast.error(t("admin.pw_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success(t("admin.pw_updated"));
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md rounded-2xl border border-border bg-white p-6 sm:p-8">
      <div className="flex items-center gap-2 text-terracotta">
        <KeyRound className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-widest">{t("admin.change_password")}</span>
      </div>
      <form onSubmit={submit} className="mt-5 space-y-4" data-testid="change-password-form">
        <div>
          <Label>{t("admin.current_password")}</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="current-password" />
        </div>
        <div>
          <Label>{t("admin.new_password")}</Label>
          <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={8} data-testid="new-password" />
          <p className="mt-1 text-xs text-muted-foreground">{t("admin.pw_hint")}</p>
        </div>
        <div>
          <Label>{t("admin.confirm_password")}</Label>
          <Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} required data-testid="confirm-password" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="change-password-submit">
          {busy ? t("common.saving") : t("admin.change_password")}
        </Button>
      </form>
    </div>
  );
}

function AdminBookings() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [psychs, setPsychs] = useState([]);
  const [err, setErr] = useState(false);
  const [f, setF] = useState({ q: "", psychologist_id: "all", status: "all", modality: "all", date_from: "", date_to: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/admin/psychologists", { params: { status: "verified" } }).then((r) => setPsychs(r.data)).catch(() => setPsychs([])); }, []);

  const load = useCallback(() => {
    const params = {};
    if (f.q) params.q = f.q;
    if (f.psychologist_id !== "all") params.psychologist_id = f.psychologist_id;
    if (f.status !== "all") params.status = f.status;
    if (f.modality !== "all") params.modality = f.modality;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/admin/appointments", { params }).then((r) => { setList(r.data); setErr(false); }).catch(() => { setList([]); setErr(true); });
  }, [f]);
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [load]);

  const BK_STATUSES = ["confirmed", "cancelled", "completed", "no_show", "rescheduled"];
  const statusLabel = (s) => (BK_STATUSES.includes(s) ? t(`admin.bk_st_${s}`) : s);
  const fmt = (utc, tz) => { try { return new Date(utc).toLocaleString(lang === "es" ? "es-ES" : "en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: tz }); } catch { return utc; } };
  const rescCount = (a) => (a.history || []).filter((h) => h.status === "rescheduled").length;

  const STATUSES = ["confirmed", "cancelled", "completed", "no_show", "rescheduled"];
  const MODALITIES = ["online", "in_person"];

  return (
    <div data-testid="admin-bookings">
      <p className="mb-4 text-xs text-muted-foreground">{t("admin.bk_note")}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("admin.bk_search")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="bk-search" />
        </div>
        <Select value={f.psychologist_id} onValueChange={(v) => set("psychologist_id", v)}>
          <SelectTrigger data-testid="bk-filter-psych"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.bk_all_psych")}</SelectItem>
            {psychs.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="bk-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.bk_all_status")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.modality} onValueChange={(v) => set("modality", v)}>
          <SelectTrigger data-testid="bk-filter-modality"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.bk_all_modality")}</SelectItem>
            {MODALITIES.map((m) => <SelectItem key={m} value={m}>{t(`common.${m}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div>
          <label className="text-xs text-muted-foreground">{t("admin.bk_from")}</label>
          <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="bk-filter-from" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("admin.bk_to")}</label>
          <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="bk-filter-to" />
        </div>
      </div>
      <div className="mt-3">
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setF({ q: "", psychologist_id: "all", status: "all", modality: "all", date_from: "", date_to: "" })} data-testid="bk-clear">
          <X className="mr-1.5 h-3.5 w-3.5" />{t("admin.bk_clear")}
        </Button>
      </div>

      <div className="mt-5">
        {err ? <p className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" data-testid="admin-bk-error">{t("admin.bk_load_error")}</p>
          : list.length === 0 ? <EmptyState icon={CalendarClock} title={t("admin.bk_none")} testId="admin-bk-empty" /> : (
          <div className="space-y-3">
            {list.map((a) => (
              <div key={a.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`admin-appt-${a.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slateink">{a.client_name} <span className="text-xs font-normal text-muted-foreground">· {a.client_email}{a.client_phone ? ` · ${a.client_phone}` : ""}</span></p>
                    <p className="text-xs text-muted-foreground">{t("admin.bk_psych")}: {a.psychologist_name} · {a.type_label}</p>
                  </div>
                  <StatusBadge status={a.status === "confirmed" ? "verified" : a.status === "cancelled" || a.status === "no_show" ? "rejected" : "pending"} label={statusLabel(a.status)} />
                </div>
                <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
                  <span className="inline-flex items-center gap-1"><CalendarClock className="h-4 w-4" />{fmt(a.start_utc, a.psych_timezone)} <span className="text-xs">({a.psych_timezone})</span></span>
                  <span className="inline-flex items-center gap-1"><Globe className="h-4 w-4" />{fmt(a.start_utc, a.client_timezone)} <span className="text-xs">({a.client_timezone})</span></span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span>{t("admin.bk_modality")}: {a.modality === "online" || a.modality === "in_person" ? t(`common.${a.modality}`) : t("common.not_provided")}</span>
                  <span className="inline-flex items-center gap-1"><Bell className="h-3.5 w-3.5" />{t("admin.bk_reminders")}: 24h {a.reminder_advance_sent ? "✓" : "—"} · 3h {a.reminder_sameday_sent ? "✓" : "—"}</span>
                  {rescCount(a) > 0 && <span>{rescCount(a)} {t("admin.bk_rescheduled")}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
